import React, { useEffect, useState } from "react";
import axios from "axios";
import { Cookie, X } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "galeriecop.cookieConsent";
const SESSION_KEY = "galeriecop.consentSessionId";

/** Anonymous session id so we can correlate a user's consent history across
 *  sessions without needing login. Generated on first visit and reused after. */
const _sessionId = () => {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
};

/** Log consent both to localStorage (so we don't show the banner again) and
 *  to the backend audit log so we have a paper trail. Backend call is
 *  fire-and-forget — a failure never re-shows the banner. */
const _record = async (kind, choices) => {
  const payload = { kind, choices: choices || null, session_id: _sessionId() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...payload, ts: new Date().toISOString() }));
  // Let the analytics loader react to the new preferences in-session.
  try { window.dispatchEvent(new CustomEvent("galeriecop:consent-updated", { detail: payload.choices })); } catch (_e) {}
  try { await axios.post(`${API}/gdpr/consent`, payload); }
  catch (_e) { /* silent — localStorage is authoritative */ }
};

/**
 * GDPR / UK-GDPR cookie consent banner.
 *
 * First-visit UX: sticky bottom card with three actions —
 *   * Accept all → analytics + marketing cookies allowed
 *   * Reject non-essential → strictly-necessary only
 *   * Manage preferences → expands panel with individual toggles
 *
 * Choice is persisted in localStorage AND mirrored to the backend audit log.
 * Any component that needs to read the choice can do:
 *   const c = JSON.parse(localStorage.getItem("galeriecop.cookieConsent") || "null")
 * The footer's "Cookie settings" link fires `galeriecop:open-cookie-banner`
 * on window to re-open the banner anytime.
 */
const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) setVisible(true);
  }, []);

  useEffect(() => {
    const openBanner = () => { setExpanded(true); setVisible(true); };
    window.addEventListener("galeriecop:open-cookie-banner", openBanner);
    return () => window.removeEventListener("galeriecop:open-cookie-banner", openBanner);
  }, []);

  if (!visible) return null;

  const acceptAll = async () => {
    await _record("cookies_accept_all", { analytics: true, marketing: true });
    setVisible(false);
  };
  const rejectAll = async () => {
    await _record("cookies_essential_only", { analytics: false, marketing: false });
    setVisible(false);
  };
  const saveCustom = async () => {
    await _record("cookies_custom", { analytics, marketing });
    setVisible(false);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 px-4 pb-4 md:pb-6" data-testid="cookie-banner">
      <div className="max-w-4xl mx-auto bg-white border border-navy-700/10 rounded-2xl shadow-2xl p-5 md:p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-coral-100 flex items-center justify-center shrink-0">
            <Cookie className="w-5 h-5 text-coral-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-serif text-navy-700 text-lg">
              We use cookies to run galeriecop and improve your experience
            </h3>
            <p className="text-navy-700/70 text-sm mt-1">
              Essential cookies keep you signed in and the site working. Analytics cookies help us
              understand which features get used. You&apos;re in control — pick what you&apos;re comfortable with.
              Read our <Link to="/privacy" className="text-coral-400 hover:text-coral-500 underline">Privacy Policy</Link>.
            </p>

            {expanded && (
              <div className="mt-4 space-y-3 border-t border-cream-200 pt-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-semibold text-navy-700 text-sm">Strictly necessary</div>
                    <div className="text-navy-700/60 text-xs">Auth session, CSRF, load balancer. Cannot be disabled.</div>
                  </div>
                  <span className="text-xs font-semibold text-navy-700/40 whitespace-nowrap">Always on</span>
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-semibold text-navy-700 text-sm">Analytics</div>
                    <div className="text-navy-700/60 text-xs">Anonymous usage stats — helps us find bugs and improve slow pages.</div>
                  </div>
                  <input
                    type="checkbox" checked={analytics}
                    onChange={(e) => setAnalytics(e.target.checked)}
                    data-testid="cookie-banner-analytics-toggle"
                    className="w-4 h-4 accent-coral-400 mt-1"
                  />
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-semibold text-navy-700 text-sm">Marketing</div>
                    <div className="text-navy-700/60 text-xs">Personalised promos and remarketing pixels. Off by default.</div>
                  </div>
                  <input
                    type="checkbox" checked={marketing}
                    onChange={(e) => setMarketing(e.target.checked)}
                    data-testid="cookie-banner-marketing-toggle"
                    className="w-4 h-4 accent-coral-400 mt-1"
                  />
                </div>
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={acceptAll}
                data-testid="cookie-banner-accept-all"
                className="bg-coral-300 hover:bg-coral-400 text-white font-semibold text-sm px-5 py-2.5 rounded-full"
              >
                Accept all
              </button>
              <button
                onClick={rejectAll}
                data-testid="cookie-banner-reject"
                className="border border-navy-700/25 text-navy-700 hover:bg-cream-100 font-semibold text-sm px-5 py-2.5 rounded-full"
              >
                Reject non-essential
              </button>
              {expanded ? (
                <button
                  onClick={saveCustom}
                  data-testid="cookie-banner-save"
                  className="border border-navy-700 text-navy-700 hover:bg-navy-700 hover:text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-colors"
                >
                  Save preferences
                </button>
              ) : (
                <button
                  onClick={() => setExpanded(true)}
                  data-testid="cookie-banner-expand"
                  className="text-navy-700/60 hover:text-navy-700 text-sm font-semibold px-2 py-2.5 underline"
                >
                  Manage preferences
                </button>
              )}
            </div>
          </div>
          <button
            onClick={rejectAll}
            aria-label="Dismiss (equivalent to Reject)"
            data-testid="cookie-banner-close"
            className="text-navy-700/40 hover:text-navy-700 shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
